import { Component, computed, DestroyRef, inject, input, OnInit } from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatFooterCell,
  MatFooterCellDef,
  MatFooterRow,
  MatFooterRowDef,
  MatHeaderCell,
  MatHeaderCellDef,
  MatRow,
  MatRowDef,
  MatTable
} from '@angular/material/table';
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ButtonComponent } from '../../../../common/components/button/button.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DialogService } from '../../../../services/dialog/dialog.service';
import { MatTooltip } from '@angular/material/tooltip';
import { MatIconButton } from '@angular/material/button';
import { SbInviteMxidDialogComponent } from '../../sb-invite-mxid-dialog/sb-invite-mxid-dialog.component';
import { UserService } from '../../../../services/user/user.service';
import { DialogChangesService } from '../../../../services/dialog/dialog-changes.service';
import { SnackbarService } from '../../../../services/snackbar/snackbar.service';
import { MediaService } from '../../../../services/media/media.service';
import { Room as ModelRoom } from '../../../../model/room';
import { RoomMember } from '../../../../model/room-member';
import { KickService } from '../../../../services/kick/kick.service';
import {
  PowerLevelEventType,
  RoomPowerLevelService
} from '../../../../services/room-power-level/room-power-level.service';
import { EventType } from 'matrix-js-sdk';
import { RoomService } from '../../../../services/room/room.service';
import { RoomListAccessService } from '../../../../services/room-list/room-list-access.service';
import { RoomMembershipState } from '../../../../../shared-api/roomMembershipState';
import { AsyncPipe } from '@angular/common';
import { RoomPowerLevel } from '../../../../model/room-power-level';
import { GeneralSettingsTestIds } from './sb-general-settings.test-ids';
import { AccountService } from '../../../../services/account/account.service';
import { StateEventService } from '../../../../services/state-event/state-event.service';

@Component({
  selector: 'app-sb-general-settings',
  imports: [
    MatIcon,
    MatFormField,
    MatInput,
    MatColumnDef,
    MatHeaderCell,
    MatCell,
    MatRow,
    MatTable,
    FormsModule,
    MatLabel,
    ButtonComponent,
    MatTooltip,
    MatIconButton,
    ReactiveFormsModule,
    MatFooterRow,
    MatFooterCell,
    SbInviteMxidDialogComponent,
    AsyncPipe,
    MatFooterCellDef,
    MatRowDef,
    MatFooterRowDef,
    MatCellDef,
    MatHeaderCellDef
  ],
  templateUrl: './sb-general-settings.component.html',
  styleUrl: './sb-general-settings.component.scss'
})
/**
 * Allgemeine Raumeinstellungen um die Daten des Raumes nachträglich Anpassen zu können.
 * Begründung: Es werden keine AFOs durch die Möglichkeit der Raumeinstellungen erfüllt.
 */
export class SbGeneralSettingsComponent implements OnInit {
  roomId = input.required<string>();
  avatarFile: File | null = null;
  room: ModelRoom | null | undefined;
  displayedColumns: string[] = ['avatar', 'name', 'timAdress', 'kick'];
  avatarUrl: string | null = null;
  userId: string | undefined;
  powerLevel: RoomPowerLevel | undefined;
  protected readonly testIds = GeneralSettingsTestIds;
  protected readonly EventType = EventType;
  protected readonly roomPowerLevelService = inject(RoomPowerLevelService);
  protected readonly kickService = inject(KickService);
  protected readonly roomService = inject(RoomService);
  protected readonly accountService = inject(AccountService);
  protected readonly dialogService = inject(DialogService);
  protected readonly destroyRef = inject(DestroyRef);
  protected readonly userService = inject(UserService);
  protected readonly mediaService = inject(MediaService);
  protected readonly stateEventService = inject(StateEventService);
  private readonly roomListAccessService = inject(RoomListAccessService);
  dataSource = computed(() => {
    return this.roomListAccessService.roomList().flatMap(room => {
      if (room.roomId === this.roomId()) {
        return room.members.filter(
          member =>
            member.membershipState === RoomMembershipState.Invite || member.membershipState === RoomMembershipState.Join
        );
      }
      return [];
    });
  });
  private readonly formBuilder = inject(FormBuilder);
  chatForm = this.formBuilder.group({
    chatname: ['', [Validators.required]],
    chattheme: ['']
  });
  private readonly snackbarService = inject(SnackbarService);
  private readonly dialogChangesService = inject(DialogChangesService);

  ngOnInit(): void {
    this.room = this.roomListAccessService.getModelRoom(this.roomId());
    this.chatForm.controls.chatname.setValue(this.room?.name ?? '');
    this.chatForm.controls.chattheme.setValue(this.room?.theme ?? '');
    this.avatarUrl = this.room?.avatarUrl ?? null;
    (async (): Promise<void> => {
      await this.userService.updateRoomData(this.roomId());
    })();

    this.userId = this.accountService.getMyMxId();
    this.powerLevel = this.room?.powerLevels;
    this.applyPowerLevels(this.powerLevel, EventType.RoomName);
    this.applyPowerLevels(this.powerLevel, EventType.RoomTopic);

    this.chatForm.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.dialogChangesService.setChanges(true);
    });
  }

  getCreator(): string | undefined | null {
    return this.room?.creator;
  }

  async kick(member: RoomMember): Promise<void> {
    if (!this.room) return;
    await this.kickService.openKickDialog(this.room.roomId, member.mxid);
  }

  async save(): Promise<void> {
    const theme = this.chatForm.controls['chattheme'].value;
    const name = this.chatForm.controls['chatname'].value;
    if (!name) {
      throw new Error('Required Paramter name was null or undefined when calling save in Settings');
    }
    if (name && this.chatForm.controls['chatname'].dirty) {
      await this.roomService.changeRoomSettings(this.roomId(), {
        name: name
      });
    }
    if (theme && this.chatForm.controls['chattheme'].dirty) {
      await this.roomService.changeRoomSettings(this.roomId(), {
        name: name,
        theme: theme
      });
    }
    if (this.avatarFile) {
      try {
        const uploadResponse = await this.mediaService.uploadAvatarToServer(this.avatarFile);
        await this.setRoomAvatar(this.roomId() ?? '', uploadResponse.content_uri);
      } catch (err: any) {
        this.snackbarService.error('Es kam zu einem Fehler beim Hochladen des Avatars');
        console.error('Could not upload or set RoomAvatar', err);
      } finally {
        this.userService.setUserData(this.roomId(), this.userService.getBlobUrl(this.avatarFile));
      }
    }
    this.snackbarService.success('Raumeinstellungen geändert');
    this.dialogChangesService.setChanges(false);
  }

  async onFileSelected(event: Event): Promise<void> {
    const selectedFile: File | null = await this.mediaService.getAndCheckAvatarFileOfEvent(event);
    if (selectedFile) {
      this.avatarFile = selectedFile;
    }
  }

  protected canSave(powerLevel: RoomPowerLevel | undefined): boolean {
    if (!powerLevel) {
      throw new Error('No RoomPowerLevel, when calling canSave');
    }
    if (!this.userId) {
      throw new Error('No userId, when calling applyPowerLevel');
    }
    const canEditName = this.roomPowerLevelService.canDoEvent(EventType.RoomName, powerLevel, this.userId);
    const canEditTheme = this.roomPowerLevelService.canDoEvent(EventType.RoomTopic, powerLevel, this.userId);
    const canEditAvatar = this.roomPowerLevelService.canDoEvent(EventType.RoomAvatar, powerLevel, this.userId);
    return canEditName || canEditAvatar || canEditTheme;
  }

  private applyPowerLevels(powerLevel: RoomPowerLevel | undefined, eventType: PowerLevelEventType): void {
    if (!powerLevel) {
      throw new Error('No RoomPowerLevel, when calling applyPowerLevels');
    }
    if (!this.userId) {
      throw new Error('No userId, when calling applyPowerLevel');
    }
    const canEdit = this.roomPowerLevelService.canDoEvent(eventType, powerLevel, this.userId);
    this.roomPowerLevelService.applyPowerLevelToFormControl(this.chatForm.controls.chatname, canEdit);
  }

  private async setRoomAvatar(roomId: string, roomAvatarUrl: string): Promise<void> {
    if (roomAvatarUrl) {
      await this.stateEventService.sendStateEventAvatar(roomId, roomAvatarUrl);
    }
  }
}
